import { toHumanReadableDate } from '@modules/_shared/utils/formatting';
import type { Translate } from '@shared/hooks/locale';
import type { IntlLocale } from '@shared/LocaleContext';

export const formatDueDate = (
  locale: IntlLocale,
  dateString: string | null | undefined,
  t: Translate,
) => {
  if (!dateString) {
    return { text: '', overdue: false };
  }
  const date = new Date(dateString);
  const now = new Date();

  // Check if date has a specific time (not midnight UTC)
  const hasTime = date.getUTCHours() !== 0 || date.getUTCMinutes() !== 0;
  const timeStr = hasTime
    ? date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
    : null;

  // Normalize to start of day for proper day-level comparison
  const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // Calculate difference in days (positive = future, negative = past)
  const diffMs = dateDay.getTime() - todayDay.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays > 0) {
    // Future (not overdue)
    if (diffDays === 1) {
      return {
        text: timeStr
          ? t('Due tomorrow at {{time}}', { time: timeStr })
          : t('Due tomorrow'),
        overdue: false,
      };
    }
    if (diffDays < 7) {
      return {
        text: t('Due in {{count}}d', { count: diffDays }),
        overdue: false,
      };
    }
    if (diffDays < 30) {
      return {
        text: t('Due in {{count}}w', { count: Math.floor(diffDays / 7) }),
        overdue: false,
      };
    }
    if (diffDays < 365) {
      return {
        text: t('Due in {{count}}mo', { count: Math.floor(diffDays / 30) }),
        overdue: false,
      };
    }
    return {
      text: t('Due in {{count}}y', { count: Math.floor(diffDays / 365) }),
      overdue: false,
    };
  }

  if (diffDays === 0) {
    return {
      text: timeStr
        ? t('Due today at {{time}}', { time: timeStr })
        : t('Due today'),
      overdue: false,
    };
  }

  // Past (overdue)
  const absDays = Math.abs(diffDays);
  if (absDays === 1) {
    return { text: t('Due yesterday'), overdue: true };
  }
  if (absDays < 7) {
    return { text: t('Due {{count}}d ago', { count: absDays }), overdue: true };
  }
  if (absDays < 30) {
    return {
      text: t('Due {{count}}w ago', { count: Math.floor(absDays / 7) }),
      overdue: true,
    };
  }
  if (absDays < 365) {
    return {
      text: t('Due {{count}}mo ago', { count: Math.floor(absDays / 30) }),
      overdue: true,
    };
  }
  return {
    text: t('Due {{count}}y ago', { count: Math.floor(absDays / 365) }),
    overdue: true,
  };
};

export const formatFullDate = (
  locale: IntlLocale,
  dateString: string | null | undefined,
) => {
  if (!dateString) {
    return null;
  }
  const date = new Date(dateString);
  const hasTime = date.getUTCHours() !== 0 || date.getUTCMinutes() !== 0;

  return toHumanReadableDate(locale, dateString, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(hasTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
};
